import {ReactElement, useState, useEffect, SyntheticEvent} from 'react';
import {useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {ResizableBox} from 'react-resizable';
import 'react-resizable/css/styles.css';

import {useTheme} from '@mui/material/styles';
import {
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Stack,
  Typography,
  Box
} from '@mui/material';
import {ChevronLeft} from '@mui/icons-material';
import {TreeView} from '@mui/x-tree-view';
import {TreeItem} from '@mui/x-tree-view/TreeItem';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

import CustomTreeView from '../treeView/CustomTreeView';
import CustomDocViewer from '../docViewer/CustomDocViewer';
import {
  DocumentsContainer,
  MainContainer,
  MainTreeViewContainer,
  MainTreeViewFileCabinetContainer,
  MainMetaInfoContainer,
  MainDocViewContainer,
  TreeViewContainer,
  MetaInfo,
  MainNoInfoContainer
} from './DocumentsComponentStyles';

import {
  getDocumentsHierarchyData,
  getFilesContent
} from '@shared/services/service-handlers/PrivateService';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import HelperUtils from '@shared/utils/HelperUtils';
import FormConstants from '@shared/constants/FormConstants';

import {
  AI_BLOB_SEARCH,
  BASE_MENU_ITEM,
  BASE_RECORD_ID
} from 'src/routes/BaseRoute';
import AuthUtils from 'src/utils/AuthUtils';
import EncryptUtils from 'src/utils/EncryptUtils';
import {LoadingScreen} from 'src/minimal-components/loading-screen';
import FileThumbnail from 'src/minimal-components/file-thumbnail/file-thumbnail';

import {useModuleContext} from '@context/ModuleContext';
import {useAppContext} from '@context/AppContext';

import CardComponent from '../../components/card/CardComponent';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import EmptyContent from '@components/empty-content/empty-content';
import SingleDocViewer from '@components/docViewer/SingleDocViewer';
import FileCabinetBreadCrumb from './FileCabinetBreadCrumb';

const ClinicalMetaInfoFields = [
  {
    id: 0,
    label: 'Name',
    data_column: 'name'
  },
  {
    id: 1,
    label: 'Encounter Number',
    data_column: 'encounter_number'
  },
  {
    id: 2,
    label: 'Encounter Date',
    data_column: 'encounter_date'
  },
  {
    id: 3,
    label: 'Document Date',
    data_column: 'document_date',
    type: 'date-time'
  },
  {
    id: 5,
    label: 'Source Document ID',
    data_column: 'source_document_id'
  },
  {
    id: 6,
    label: 'Owner',
    data_column: 'owner'
  },
  {
    id: 7,
    label: 'Verified By',
    data_column: 'verified_by'
  },
  {
    id: 8,
    label: 'Reviewed By',
    data_column: 'reviewed_by'
  },
  {
    id: 9,
    label: 'Status',
    data_column: 'status'
  },
  {
    id: 10,
    label: 'Signed By',
    data_column: 'signed_by'
  },
  {
    id: 11,
    label: 'Uploaded By',
    data_column: 'uploaded_by'
  },
  {
    id: 12,
    label: 'Version No',
    data_column: 'version_no'
  },
  {
    id: 13,
    label: 'VersionDate',
    data_column: 'version_date',
    type: 'date-time'
  }
];
const PharmacyMetaInfoFields = [
  {
    id: 0,
    label: 'Name',
    data_column: 'name'
  },
  {
    id: 1,
    label: 'Prescription Number',
    data_column: 'prescription_number'
  },
  {
    id: 2,
    label: 'Prescription Date',
    data_column: 'prescription_date'
  },
  {
    id: 3,
    label: 'Document Date',
    data_column: 'document_date',
    type: 'date-time'
  },
  {
    id: 5,
    label: 'Source Document ID',
    data_column: 'source_document_id'
  },
  {
    id: 6,
    label: 'Owner',
    data_column: 'owner'
  },
  {
    id: 7,
    label: 'Verified By',
    data_column: 'verified_by'
  },
  {
    id: 8,
    label: 'Reviewed By',
    data_column: 'reviewed_by'
  },
  {
    id: 9,
    label: 'Status',
    data_column: 'status'
  },
  {
    id: 10,
    label: 'Signed By',
    data_column: 'signed_by'
  },
  {
    id: 11,
    label: 'Uploaded By',
    data_column: 'uploaded_by'
  }
];
const PAMetaInfoFields = [
  {
    id: 0,
    label: 'Name',
    data_column: 'name'
  },
  {
    id: 1,
    label: 'Account Number',
    data_column: 'account_number'
  },
  {
    id: 2,
    label: 'Document Date',
    data_column: 'document_date',
    type: 'date-time'
  },
  {
    id: 3,
    label: 'Source Document ID',
    data_column: 'source_document_id'
  },
  {
    id: 4,
    label: 'Owner',
    data_column: 'owner'
  },
  {
    id: 5,
    label: 'Verified By',
    data_column: 'verified_by'
  },
  {
    id: 6,
    label: 'Reviewed By',
    data_column: 'reviewed_by'
  },
  {
    id: 7,
    label: 'Status',
    data_column: 'status'
  },
  {
    id: 8,
    label: 'Signed By',
    data_column: 'signed_by'
  },
  {
    id: 9,
    label: 'Uploaded By',
    data_column: 'uploaded_by'
  }
];
const FCMetaInfoFields = [
  {
    id: 0,
    label: 'Name',
    data_column: 'name'
  },
  {
    id: 2,
    label: 'Document Date',
    data_column: 'document_date',
    type: 'date-time'
  },
  {
    id: 3,
    label: 'Source Document ID',
    data_column: 'source_document_id'
  },
  {
    id: 4,
    label: 'Owner',
    data_column: 'owner'
  },
  {
    id: 5,
    label: 'Verified By',
    data_column: 'verified_by'
  },
  {
    id: 6,
    label: 'Reviewed By',
    data_column: 'reviewed_by'
  },
  {
    id: 7,
    label: 'Status',
    data_column: 'status'
  },
  {
    id: 8,
    label: 'Signed By',
    data_column: 'signed_by'
  },
  {
    id: 9,
    label: 'Uploaded By',
    data_column: 'uploaded_by'
  },
  {
    id: 10,
    label: 'Version No',
    data_column: 'version_no'
  },
  {
    id: 11,
    label: 'VersionDate',
    data_column: 'version_date',
    type: 'date-time'
  },
  {
    id: 12,
    label: 'Physical Name',
    data_column: 'physical_name'
  },
  {
    id: 13,
    label: 'Type',
    data_column: 'document_type'
  },
  {
    id: 14,
    label: 'Document Identifiers',
    is_section_header: true
  },
  {
    id: 15,
    label: 'Document Identifier 1',
    data_column: 'document_identifier_1'
  },
  {
    id: 16,
    label: 'Document Identifier 2',
    data_column: 'document_identifier_2'
  },
  {
    id: 17,
    label: 'Document Identifier 3',
    data_column: 'document_identifier_3'
  },
  {
    id: 18,
    label: 'Document Identifier 4',
    data_column: 'document_identifier_4'
  },
  {
    id: 19,
    label: 'Document Identifier 5',
    data_column: 'document_identifier_5'
  }
];
const DefaultMetaInfoFields = [
  {
    id: 0,
    label: 'Name',
    data_column: 'name'
  },
  {
    id: 2,
    label: 'Document Date',
    data_column: 'document_date',
    type: 'date-time'
  },
  {
    id: 3,
    label: 'Source Document ID',
    data_column: 'source_document_id'
  },
  {
    id: 4,
    label: 'Owner',
    data_column: 'owner'
  },
  {
    id: 5,
    label: 'Verified By',
    data_column: 'verified_by'
  },
  {
    id: 6,
    label: 'Reviewed By',
    data_column: 'reviewed_by'
  },
  {
    id: 7,
    label: 'Status',
    data_column: 'status'
  },
  {
    id: 8,
    label: 'Signed By',
    data_column: 'signed_by'
  },
  {
    id: 9,
    label: 'Uploaded By',
    data_column: 'uploaded_by'
  },
  {
    id: 10,
    label: 'Version No',
    data_column: 'version_no'
  },
  {
    id: 11,
    label: 'VersionDate',
    data_column: 'version_date',
    type: 'date-time'
  }
];

interface Document {
  ID: number | string;
  LegacyApp: string;
  LegacyMRN: string;
  SourceDocumentID: string;
  ParentDocumentTypeID?: null | number;
  FileExtensions: string[];
  IsNotes: boolean[];
  Name: string;
  Description: string;
  IsActive: boolean;
  FULL_PATH: string;
  ALPHA_SEQ_PATH: string;
  order: number;
  IsMultiPage: string;
  Sequence: number[];
  DocumentTypeID: number;
  ChildNodes?: any[];
  Code?: string;
  HasDocuments?: boolean;
  Count?: number;
  ConnectionStrings?: any[];
  Containers?: any[];
  AccountNumber?: string | null;
  EncounterNumber?: string | null;
  EncounterDate?: string | null;
  PrescriptionNumber?: string | null;
  PrescriptionDate?: string | null;
  DocumentDate?: string | null;
  Owner?: string | null;
  VerifiedBy?: string | null;
  ReviewedBy?: string | null;
  Status?: any;
  SignedBy?: string | null;
  UploadedBy?: string | null;
  Version?: string | null;
  VersionDate?: string | null;
  PhysicalName?: string | null;
  physical_name?: string | null;
  documentType?: string | null;
  DocumentType?: string | null;
  Type?: string | null;
  FileType?: string | null;
  MimeType?: string | null;
  ContentType?: string | null;
  DocumentIdentifier1?: string | null;
  DocumentIdentifier2?: string | null;
  DocumentIdentifier3?: string | null;
  DocumentIdentifier4?: string | null;
  DocumentIdentifier5?: string | null;
  documentIdentifier1?: string | null;
  documentIdentifier2?: string | null;
  documentIdentifier3?: string | null;
  documentIdentifier4?: string | null;
  documentIdentifier5?: string | null;
}
interface DocumentFolder {
  ID: number | string;
  ParentDocumentTypeID: null | number;
  DocumentTypeDomainID: number;
  Code: string;
  Name: string;
  Description: string;
  isActive: string;
  Sequence: string;
  FULL_PATH: string;
  ChildNodes: any[];
  HasDocuments: boolean;
  FileExtensions: [];
  SourceDocumentID?: string;
  DocumentsData?: any[];
  FilePaths?: any[];
  IsNotes?: boolean[];
}
interface DocumentsProps {
  subModuleUIData: any;
  subModuleData: any;
  module: string;
  sub_module: string;
  patient_id: string | any;
  legacyApp?: string;
  unique_id?: string;
  procedure_type?: string;
  accession_number?: string;
  specimen_number_id?:string;
  ParentLegacyValue?: string;
  folder_id?: string;
  date_range?: object;
  rowData?: any;
  height?: any;
  top?: string;
}

const FileCabinetDocumentsComponent = (props: DocumentsProps): ReactElement => {
  const theme = useTheme();
  const {t} = useTranslation();
  const [shoulddisplaymetainfo, setShoulddisplaymetainfo] =
    useState<boolean>(false);
  const [documentFolders, setDocumentFolders] = useState<any[]>([]);
  const [documentFoldersdata, setDocumentFoldersdata] = useState<any[]>([]);
  const [dicomFiles, setDicomFiles] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [docData, setDocData] = useState<Document | null>(null);
  const [cardValues, setCardValues] = useState<any>([]);
  const [docDownloadData, setDocDownloadData] = useState<string[] | null>([]);
  const {enqueueSnackbar} = useSnackbar();
  const [iframeSrc, setIframeSrc] = useState<string | null>('');
  const [isNodeToggled, setIsNodeToggle] = useState<boolean>(false);
  const [docViewerWidth, setDocViewerWidth] = useState<number>(1500);
  const {
    appState: {applicationName}
  } = useAppContext();
  const {moduleState} = useModuleContext();
  const {isBH, isBHReason}: any = moduleState;

  const menuItem = new URLSearchParams(location?.search).get(BASE_MENU_ITEM);

  const {
    subModuleUIData,
    ParentLegacyValue,
    height,
    top,
    procedure_type,
    accession_number,specimen_number_id
  } = props;

  const recordId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );

  const aiBlobSearch = EncryptUtils.getDecryptedData(
    location?.search,
    AI_BLOB_SEARCH
  );
  const parsedAiBlobSearch = aiBlobSearch ? JSON.parse(aiBlobSearch) : {};

  const getFirstAvailableValue = (...values: any[]) =>
    values.find(
      value =>
        value !== undefined &&
        value !== null &&
        !(typeof value === 'string' && value.trim() === '')
    );

  const getReadableDocumentType = (doc: Document | null) => {
    const selectedDocumentType = getFirstAvailableValue(
      doc?.documentType,
      doc?.DocumentType,
      doc?.Type
    );

    if (selectedDocumentType && typeof selectedDocumentType === 'string') {
      return selectedDocumentType;
    }

    const rawType = getFirstAvailableValue(
      doc?.FileType,
      doc?.MimeType,
      doc?.ContentType,
      Array.isArray(doc?.FileExtensions) ? doc?.FileExtensions?.[0] : undefined
    );

    if (!rawType || typeof rawType !== 'string') {
      return '';
    }

    const normalizedType = rawType.toLowerCase();
    const mimeToLabelMap: Record<string, string> = {
      'image/jpeg': 'JPEG Image',
      'image/jpg': 'JPEG Image',
      'image/png': 'PNG Image',
      'image/gif': 'GIF Image',
      'image/tiff': 'TIFF Image',
      'application/pdf': 'PDF Document',
      'application/msword': 'Word Document',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        'Word Document',
      'application/vnd.ms-excel': 'Excel Spreadsheet',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        'Excel Spreadsheet',
      'application/vnd.ms-powerpoint': 'PowerPoint Presentation',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        'PowerPoint Presentation',
      'text/plain': 'Text Document'
    };

    if (mimeToLabelMap[normalizedType]) {
      return mimeToLabelMap[normalizedType];
    }

    const extensionToLabelMap: Record<string, string> = {
      jpg: 'JPEG Image',
      jpeg: 'JPEG Image',
      png: 'PNG Image',
      gif: 'GIF Image',
      tif: 'TIFF Image',
      tiff: 'TIFF Image',
      pdf: 'PDF Document',
      doc: 'Word Document',
      docx: 'Word Document',
      xls: 'Excel Spreadsheet',
      xlsx: 'Excel Spreadsheet',
      ppt: 'PowerPoint Presentation',
      pptx: 'PowerPoint Presentation',
      txt: 'Text Document'
    };

    return extensionToLabelMap[normalizedType] || rawType;
  };

  useEffect(() => {
    if (parsedAiBlobSearch?.is_ai_blob_search) {
      // Reset all states
      setShoulddisplaymetainfo(false);
      setDocumentFolders([]);
      setDocumentFoldersdata([]);
      setDicomFiles([]);
      setDocData(null);
      setCardValues([]);
      setDocDownloadData([]);
      setIframeSrc('');
      setIsNodeToggle(false);
    }
  }, [parsedAiBlobSearch?.is_ai_blob_search]);

  const {
    documents_versions_sql: documentVersionSQL,
    document_version_data_sql: documentVersionDataSQL,
    document_data_sql: documentDataSql,
    document_folders_sql: documentFolderSql,
    legit_documents_folders_sql: legitDocumentsFoldersSql,
    documents_count_sql: documentsCountSql,
    document_folders_contents: folderContentsQuery,
    filter_schema_clause: filterSchemaClause,
    variables,
    actions,
    // show_checkbox: showCheckbox,
    hide_document_viewer: hideDocumentViewer
  } = subModuleUIData;
  const getLocaleStorageData = AuthUtils.getSelectedCheckBoxData();

  useEffect(() => {
    const getDocumentFolders = async () => {
      try {
        const sql = documentFolderSql
          ? {
              documentFolderSql: `${documentFolderSql}`,
              legitDocumentFoldersSql: `${legitDocumentsFoldersSql}`,
              documentsCountSql: `${documentsCountSql}`
            }
          : {
              folderContentsQuery
            };
        const auditObj = {
          is_audit: true,
          audit_event_description: FormConstants.COMMON.VIEWED_DOCUMENT,
          rowData: props?.rowData
        };

        const tempDataColumns: any = {};

        if (props?.rowData) {
          const tempObj = props.rowData;
          for (const key in tempObj) {
            if (Object.hasOwn(tempObj, key)) {
              tempDataColumns[key] = tempObj[key];
            }
          }
        }

        const data: any = {
          sql: sql,
          variables: variables,
          filterSchemaClause: filterSchemaClause,
          // folder_id: props?.folder_id ?? '',
          date_range: props?.date_range ?? {},
          audit_identifier: AuthUtils.getAuditIdentifier(),
          audit_identifier_type: AuthUtils.getAuditIdentifierType(),
          audit_legacy_app: AuthUtils.getAuditLegacyApp(),
          isBH_selected: !isBHReason?.length && isBH == false ? true : isBH,
          isBH_reason: isBHReason,
          application_name: applicationName,
          legacyMRN: recordId,
          procedure_type,
          accession_number,specimen_number_id
        };

        data.sql_data = {...auditObj, ...tempDataColumns};

        if (ParentLegacyValue) {
          data.parentLegacyValue = ParentLegacyValue;
        }
        if (subModuleUIData.filter_schema_clause) {
          data.filter_schema_clause = subModuleUIData.filter_schema_clause;
        }
        if (
          subModuleUIData?.is_filter_sql_required &&
          getLocaleStorageData.length > 0
        ) {
          data.filter_data = getLocaleStorageData;
        }
        const encryptRequestData = EncryptUtils.encryptedData(data);
        setIsLoading(true);
        const response = documentFolderSql
          ? await getDocumentsHierarchyData(
              props?.module,
              props?.sub_module,
              props?.patient_id,
              {tk: encryptRequestData},
              props?.legacyApp,
              props?.unique_id,
              menuItem ?? ''
            )
          : await getFilesContent(
              props?.module,
              props?.sub_module,
              props?.patient_id,
              props?.unique_id || '',
              {tk: encryptRequestData},
              props?.legacyApp,
              menuItem ?? ''
            );

        const {
          dicom_files: dicomFiles,
          documents_hierarchy: documentsHierarchy,
          documents: documents
        } = response;
        // console.log('documentsHierarchy-->', documentsHierarchy);
        setDicomFiles(dicomFiles);
        setDocumentFolders(documentsHierarchy ? documentsHierarchy : documents);
        setDocumentFoldersdata(
          documentsHierarchy ? documentsHierarchy : documents
        );
        setIsLoading(false);
      } catch (e) {
        const errMessage = getErrorMessage(e);
        if (Array.isArray(documentFolders) && documentFolders.length === 0) {
          setIsLoading(false);
        } else {
          enqueueSnackbar(errMessage, SnackBarConfig.getError());
        }
      }
    };
    getDocumentFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ParentLegacyValue, parsedAiBlobSearch?.is_ai_blob_search]);

  useEffect(() => {
    if (docData && Object.keys(docData)?.length) {
      if (setIframeSrc) {
        setIframeSrc(null);
      }
      // console.log('docData-->',docData)
      const tempCardValues = [
        {
          name: docData?.Name,
          source_document_id: docData?.SourceDocumentID,
          account_number: docData?.AccountNumber,
          encounter_number: docData?.EncounterNumber,
          prescription_number: docData?.PrescriptionNumber,
          prescription_date: HelperUtils.formatData(
            docData?.PrescriptionDate || '',
            FormConstants.CONFIG_FIELDS_TYPE.DATE_TIME
          ),
          encounter_date: HelperUtils.formatData(
            docData?.EncounterDate || '',
            FormConstants.CONFIG_FIELDS_TYPE.DATE_TIME
          ),
          document_date: HelperUtils.formatData(
            docData?.DocumentDate || '',
            FormConstants.CONFIG_FIELDS_TYPE.DATE_TIME
          ),
          owner: docData?.Owner,
          verified_by: docData?.VerifiedBy,
          reviewed_by: docData?.ReviewedBy,
          status: docData?.Status,
          signed_by: docData?.SignedBy,
          uploaded_by: docData?.UploadedBy,
          version_no: docData?.Version,
          version_date: HelperUtils.formatData(
            docData?.VersionDate || '',
            FormConstants.CONFIG_FIELDS_TYPE.DATE_TIME
          ),
          physical_name: getFirstAvailableValue(
            docData?.PhysicalName,
            docData?.physical_name,
            docData?.Name
          ),
          document_type: getReadableDocumentType(docData),
          document_identifier_1: getFirstAvailableValue(
            docData?.DocumentIdentifier1,
            docData?.documentIdentifier1
          ),
          document_identifier_2: getFirstAvailableValue(
            docData?.DocumentIdentifier2,
            docData?.documentIdentifier2
          ),
          document_identifier_3: getFirstAvailableValue(
            docData?.DocumentIdentifier3,
            docData?.documentIdentifier3
          ),
          document_identifier_4: getFirstAvailableValue(
            docData?.DocumentIdentifier4,
            docData?.documentIdentifier4
          ),
          document_identifier_5: getFirstAvailableValue(
            docData?.DocumentIdentifier5,
            docData?.documentIdentifier5
          )
        }
      ];

      setCardValues(tempCardValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [docData?.SourceDocumentID, docData?.Version]);
    }, [docData]);
// console.log('docData123-->',docData)  

  const getLoaderComponent = (): JSX.Element => (
    <MainNoInfoContainer height={height}>
      <LoadingScreen />
    </MainNoInfoContainer>
  );
  useEffect(() => {
    if (shoulddisplaymetainfo) {
      setDocViewerWidth(700);
    } else {
      setDocViewerWidth(1500);
    }
  }, [shoulddisplaymetainfo]);
  const handleResizeDocViewer = (e: any, data: any) => {
    const newDocViewerWidth = data.size.width;
    setDocViewerWidth(newDocViewerWidth);
  };

  const setWidth = () => {
    if (hideDocumentViewer === undefined) {
      return props.subModuleUIData.layout.browser.grid_columns;
    }

    return 12;
  };
  const getMetaInfo = () => (
    <CardComponent
      sectionUIData={{
        label: 'Document Details'
      }}
      fields={
        props?.module === FormConstants.MODULE.PATIENT_ACCOUNTING
          ? PAMetaInfoFields
          : props?.module === FormConstants.MODULE.CLINICAL
            ? ClinicalMetaInfoFields
            : props?.module === FormConstants.MODULE.PHARMACY
              ? PharmacyMetaInfoFields
              : props?.module === FormConstants.MODULE.FILECABINET
                ? FCMetaInfoFields
                : DefaultMetaInfoFields
      }
      is_colon_needed={true}
      data={cardValues}
      is_border_required={false}
      component_type="document"
      columns_per_row={1}
      module={props?.module}
      sub_module={props?.sub_module}
    />
  );

  const handleFileClick = (file: any) => {
    setIframeSrc(file);
  };

  const handleFolderChange = async (event: SyntheticEvent, nodes: string[]) => {
    setIsNodeToggle(nodes.includes('0') && !isNodeToggled);
  };

  const getTreeComponent = (): JSX.Element => (
    <MainTreeViewContainer item md={setWidth()} xs={8}>
      <TreeViewContainer
        isdochidden={hideDocumentViewer}
        shoulddisplaymetainfo={`${shoulddisplaymetainfo}`}
        height={height}>
        {documentFoldersdata.map((eachNode: Document) => (
          <CustomTreeView
            hideDocumentViewer={hideDocumentViewer}
            key={eachNode.ID}
            node={eachNode}
            setDocData={setDocData}
            docDownloadData={docDownloadData || []}
            cardValues={cardValues}
            folderContentsQuery={subModuleUIData?.document_folders_contents}
            module={props?.module}
            subModule={props?.sub_module}
            userId={props?.patient_id}
            legacyApp={props?.legacyApp}
            sqlVariables={subModuleUIData?.variables}
            unique_id={props?.unique_id}
            menuItem={menuItem || ''}
            sectionUIData={subModuleUIData}
            date_range={props?.date_range ?? {}}
            setIframeSrc={setIframeSrc}
            recordId={recordId}
            procedure_type={procedure_type}
            accession_number={accession_number}
            specimen_number_id={specimen_number_id}
          />
        ))}
        {dicomFiles && dicomFiles.length > 0 && (
          <TreeView
            aria-label="file system navigator"
            defaultCollapseIcon={
              <ArrowDropDownIcon style={{color: theme.palette.text.disabled}} />
            }
            defaultExpandIcon={
              <ArrowRightIcon style={{color: theme.palette.text.disabled}} />
            }
            defaultEndIcon={''}
            onNodeToggle={handleFolderChange}>
            <TreeItem
              nodeId="0"
              label={
                <Stack direction="row" alignItems="center" spacing={1} my={0.5}>
                  <FileThumbnail file={'folder'} sx={{width: 15, height: 15}} />
                  <Typography
                    noWrap
                    variant="inherit"
                    sx={{
                      // maxWidth: 360,
                      cursor: 'pointer',
                      textOverflow: 'clip',
                      overflow: 'unset',
                      paddingRight: '1vh',
                      color: theme.palette.common.black,
                      ...(isNodeToggled && {
                        fontWeight: 'bold'
                      })
                    }}>
                    {'Dicom Files'}
                  </Typography>
                </Stack>
              }>
              {dicomFiles &&
                dicomFiles.map((dicomFile: any, index: any) => (
                  <TreeItem
                    key={index}
                    nodeId={dicomFile?.id}
                    label={
                      <div onClick={() => handleFileClick(dicomFile?.UrlPath)}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          my={0.5}>
                          <FileThumbnail
                            file={'file'}
                            tooltip={true}
                            sx={{width: 15, height: 15}}
                          />

                          <Typography
                            noWrap
                            variant="inherit"
                            sx={{
                              // maxWidth: 360,
                              cursor: 'pointer',
                              textOverflow: 'clip',
                              overflow: 'unset',
                              paddingRight: '1vh',
                              color: theme.palette.common.black
                            }}>
                            {dicomFile?.Filename}
                          </Typography>
                        </Stack>
                      </div>
                    }
                  />
                ))}
            </TreeItem>
          </TreeView>
        )}
      </TreeViewContainer>
      <MainMetaInfoContainer shoulddisplaymetainfo={`${shoulddisplaymetainfo}`}>
        <ListItemButton
          sx={{
            minHeight: 30,
            padding: 0,
            mb: '0.5rem'
          }}
          onClick={() => setShoulddisplaymetainfo(false)}>
          <ListItemIcon
            sx={{
              minWidth: 0,
              justifyContent: 'center'
            }}>
            <ChevronLeft color="primary" />
          </ListItemIcon>
          <ListItemText
            primaryTypographyProps={{
              style: {
                color: theme.palette.primary.main
              },
              fontSize: 14
            }}>
            Back to Navigation
          </ListItemText>
        </ListItemButton>
        <MetaInfo>{getMetaInfo()}</MetaInfo>
      </MainMetaInfoContainer>
    </MainTreeViewContainer>
  );

  const getDocumentViewerComponent = (): JSX.Element => (
    <ResizableBox
      width={docViewerWidth}
      axis="x" // Allow resizing horizontally only
      minConstraints={[700, Infinity]} // Min width constraint for the document viewer
      maxConstraints={[1500, Infinity]} // Max width of the document viewer based on container size
      onResize={handleResizeDocViewer} // Handle the resize event
      resizeHandles={shoulddisplaymetainfo ? ['w'] : undefined}
      // Only allow resizing from the left (dragging left edge)
      style={{display: 'flex', flex: 1, width: '100%'}}>
      <MainDocViewContainer
        item
        md={12}
        // md={props.subModuleUIData.layout.viewer.grid_columns}
        xs={12}>
        {iframeSrc ? (
          <Box mt={2} p={5}>
            <iframe
              src={iframeSrc}
              title={iframeSrc}
              style={{width: '52vw', height: '75vh', border: '0'}}
            />
          </Box>
        ) : (
          <>
            {!docData && isLoading && <div id="empty-div" />}
            {!docData && !isLoading && documentFolders?.length !== 0 && (
              <EmptyContent
                title="No file or folder selected"
                description="Select a file or folder to read"
                imgUrl="/assets/icons/empty/ic_email_selected.svg"
                sx={{
                  borderRadius: 1.5,
                  bgcolor: 'background.default'
                }}
              />
            )}
            {docData && !isLoading && (
              <CustomDocViewer
                docData={docData}
                setDocData={setDocData}
                documentVersionSQL={documentVersionSQL}
                documentVersionDataSQL={documentVersionDataSQL}
                documentDataSql={documentDataSql}
                variables={variables}
                filterSchemaClause={filterSchemaClause}
                docDownloadData={docDownloadData}
                shoulddisplaymetainfo={shoulddisplaymetainfo}
                setDocDownloadData={setDocDownloadData}
                setShoulddisplaymetainfo={setShoulddisplaymetainfo}
                cardValues={cardValues}
                customDocViewerActions={actions}
                PatientID={props?.patient_id}
                height={height}
                top={top}
                recordId={recordId}
                procedure_type={procedure_type}
                accession_number={accession_number}
                specimen_number_id={specimen_number_id}
              />
            )}
          </>
        )}
      </MainDocViewContainer>
    </ResizableBox>
  );

  const getTreeAndDocumentViewerComponent = (): JSX.Element => {
    if (!documentFolders.length) {
      return (
        <MainNoInfoContainer height={height}>
          <EmptyContent
            title={t('PRIVATE.DOCUMENT.NO_DATA', {ns: 'private'})}
          />
        </MainNoInfoContainer>
      );
    }

    if (!docData && !isLoading && documentFolders.length !== 0) {
      AuthUtils.clearSelectedDocID();
    }

    return (
      <Box
        sx={{
          width: '100%'
        }}>
        <Box
          sx={{
            display: 'flex',
            width: '100%'
          }}>
          {!docData && isLoading && <div id="empty-div" />}
          <FileCabinetBreadCrumb
            documentFolders={documentFolders}
            setDocData={setDocData}
            setDocumentFolders={setDocumentFolders}
            subModuleUIData={props?.subModuleUIData}
            subModuleData={[]}
            module={props?.module}
            sub_module={'null'}
            patient_id={''}
            legacyApp={ParentLegacyValue}
            unique_id={''}
            ParentLegacyValue={ParentLegacyValue}
            date_range={props?.date_range}
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            width: '100%'
          }}>
          <MainTreeViewFileCabinetContainer item md={setWidth()} xs={8}>
            <MainMetaInfoContainer
              shoulddisplaymetainfo={`${shoulddisplaymetainfo}`}>
              <ListItemButton
                sx={{
                  minHeight: 30,
                  padding: 0,
                  mb: '0.5rem'
                }}
                onClick={() => setShoulddisplaymetainfo(false)}>
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    justifyContent: 'center'
                  }}>
                  <ChevronLeft color="primary" />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{
                    style: {
                      color: theme.palette.primary.main
                    },
                    fontSize: 14
                  }}>
                  Back to Navigation
                </ListItemText>
              </ListItemButton>
              <MetaInfo>{getMetaInfo()}</MetaInfo>
            </MainMetaInfoContainer>
          </MainTreeViewFileCabinetContainer>
          <MainContainer height={height}>
            {parsedAiBlobSearch?.is_ai_blob_search ? (
              <MainDocViewContainer item xs={12}>
                <SingleDocViewer
                  sectionUIData={subModuleUIData?.sub_sections[0]}
                  customDocViewerActions={
                    subModuleUIData?.sub_sections[0]?.actions
                  }
                  module={props?.module}
                  sub_module={props?.sub_module}
                  refresh={true}
                  docDownloadData={docDownloadData}
                  setDocDownloadData={setDocDownloadData}
                  shoulddisplaymetainfo={shoulddisplaymetainfo}
                  setShoulddisplaymetainfo={setShoulddisplaymetainfo}
                  height={'50vh'}
                />
              </MainDocViewContainer>
            ) : hideDocumentViewer ? (
              ''
            ) : (
              getDocumentViewerComponent()
            )}
          </MainContainer>
        </Box>
        {/* <Box
          sx={{
            display: 'flex',
            width: '100%'
          }}>
          {!parsedAiBlobSearch?.is_ai_blob_search && getTreeComponent()}
          <MainContainer height={height}>
            {/* AI BLOB SEARCH */}
        {/* {parsedAiBlobSearch?.is_ai_blob_search ? (
              <MainDocViewContainer item xs={12}>
                <SingleDocViewer
                  sectionUIData={subModuleUIData?.sub_sections[0]}
                  customDocViewerActions={
                    subModuleUIData?.sub_sections[0]?.actions
                  }
                  module={props?.module}
                  sub_module={props?.sub_module}
                  refresh={true}
                  docDownloadData={docDownloadData}
                  setDocDownloadData={setDocDownloadData}
                  shoulddisplaymetainfo={shoulddisplaymetainfo}
                  setShoulddisplaymetainfo={setShoulddisplaymetainfo}
                  height={'50vh'}
                />
              </MainDocViewContainer>
            ) : hideDocumentViewer ? (
              ''
            ) : (
              getDocumentViewerComponent()
            )}
          </MainContainer> */}
        {/* </Box>
         */}
      </Box>
    );
  };

  return (
    <DocumentsContainer>
      {isLoading ? getLoaderComponent() : getTreeAndDocumentViewerComponent()}
    </DocumentsContainer>
  );
};

export default FileCabinetDocumentsComponent;
